import styles from './Footer.module.scss';

import cx from 'classnames';
import React, { useEffect, useState, CSSProperties, FC } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid } from 'components/Grid/Grid';
import type { Footer as FooterTypes } from 'api/contentful/contentTypes.cloverdotcom';
import { GridContainer } from 'components/GridContainer/GridContainer';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { FooterSecondaryNavTabletDesktop } from './FooterSecondaryNavTabletDesktop/FooterSecondaryNavTabletDesktop';
import { FooterSecondaryNavMobile } from './FooterSecondaryNavMobile/FooterSecondaryNavMobile';
import { useContentfulFooter } from './UseContentfulFooter';
import { FOOTER_TEST_ID } from './Footer.const';
import cloverLogoPurple from 'images/icons/clover-logo-purple.svg';
import { LocalePickerAndSocialLinks } from './LocalePickerAndSocialLinks/LocalePickerAndSocialLinks';
import { FooterLegalLinks } from './FooterLegalLinks/FooterLegalLinks';
import { LogoLink } from 'components/LogoLink/LogoLink';
import { useContentfulLegalDisclaimers } from './useContentfulLegalDisclaimers';

export const Footer: FC = () => {
  const {
    primaryNavItems,
    primaryBackgroundColor,
    primaryBackgroundImage,
    primaryBackgroundSmallImage,
    secondaryNavItems: defaultSecondaryNavItems,
    socialLinks: defaultSocialLinks,
    legalLinks,
    ...data
  } = useContentfulFooter() || {};
  const legalDisclaimers = useContentfulLegalDisclaimers();
  const { pathname } = useLocation();
  const isCompact = pathname === redesignV2RoutePath.CART;
  const purchasePath = `/${pathname.split('/')[1]}`;
  const isPurchase = purchasePath === redesignV2RoutePath.PURCHASE;
  const isVisible = isPurchase || isCompact;
  const secondaryNavItems = isVisible ? undefined : defaultSecondaryNavItems;
  const socialLinks = isVisible ? undefined : defaultSocialLinks;

  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isTablet = browser.is.tablet;
  const isDesktopOrLarge = browser.is.desktop;
  const [primaryItemsHeight, setPrimaryItemsHeight] = useState(0);
  const [secondaryItemsHeight, setSecondaryItemsHeight] = useState(0);

  useEffect(() => {
    setPrimaryItemsHeight(0);
    setSecondaryItemsHeight(0);
  }, [isVisible]);

  return (
    <footer
      data-testid={FOOTER_TEST_ID.footer}
      className={cx(styles.footer, isVisible && 'is-compact')}
      style={getStyle(data, primaryItemsHeight, secondaryItemsHeight)}
    >
      <GridContainer gutter>
        <Grid item xs={12} data-testid={FOOTER_TEST_ID.footerCloverLogo}>
          <Grid container className={styles.logo}>
            <LogoLink cloverLogo={cloverLogoPurple} />
          </Grid>
        </Grid>
        {isMobile && <FooterSecondaryNavMobile items={secondaryNavItems} />}
        {(isDesktopOrLarge || isTablet) && (
          <FooterSecondaryNavTabletDesktop
            items={secondaryNavItems}
            onItemsResizeUpdate={isVisible ? undefined : ({ height }) => setSecondaryItemsHeight(height)}
          />
        )}
      </GridContainer>
      <Grid item xs={12}>
        <Grid container>
          <LocalePickerAndSocialLinks socialLinks={socialLinks} />
        </Grid>
      </Grid>
      {legalLinks && legalLinks.length > 0 && (
        <Grid item xs={12}>
          <FooterLegalLinks
            items={legalLinks}
            className={styles.legalLinks}
            pageLegalDisclaimers={legalDisclaimers}
          />
        </Grid>
      )}
    </footer>
  );
};

Footer.displayName = 'Footer';

const getStyle = (
  {
    backgroundColor,
    primaryFontColor,
    primaryFontSize,
    primaryMobileFontSize,
    secondaryFontColor,
    secondaryFontSize,
    legalFontColor,
    legalFontSize,
    localePickerFontColor,
    localePickerFontSize,
  }: FlatEntry<FooterTypes>,
  primaryItemsHeight: number,
  secondaryItemsHeight: number,
): CSSProperties =>
  ({
    backgroundColor,
    '--primary-items-height': primaryItemsHeight,
    '--primary-font-color': primaryFontColor,
    '--primary-font-size': primaryFontSize,
    '--primary-mobile-font-size': primaryMobileFontSize,
    '--secondary-items-height': secondaryItemsHeight,
    '--secondary-font-color': secondaryFontColor,
    '--secondary-font-size': secondaryFontSize,
    '--legal-font-color': legalFontColor,
    '--legal-font-size': legalFontSize,
    '--locale-picker-font-color': localePickerFontColor,
    '--locale-picker-font-size': localePickerFontSize,
  } as CSSProperties);
